import { Card, CardBody, CardFooter, CardHeader } from '@PorkyCol/cards';
import { Avatar, Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { dittoTheme } from '@PorkyCol/theme';
import React, { useCallback, useEffect, useState } from 'react';
import { OrderDetailStatus } from '@modules/orders/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { changeStatusOrderDetail } from '@/services';
import dayjs from 'dayjs';
import { useDrawers } from '@hooks/useDrawers';
import { useCronometreOrder } from '@hooks/useCronometreOrder';
import { Stopwatch } from '@commons/stopwatch';

type Props = {
  table: string;
  orderId: string;
  orderDetail: any;
  prevStatus: OrderDetailStatus;
  nextStatus: OrderDetailStatus;
};

export const CardOrderDetail = ({ table, orderId, orderDetail, nextStatus, prevStatus }: Props) => {
  const queryClient = useQueryClient();
  const { onOpen, onChangeLoading } = useDrawers();
  const { mutateAsync } = useMutation({
    mutationFn: changeStatusOrderDetail,
    mutationKey: ['orders', 'cooking'],
    onSuccess: (dataSuccess: any) => {
      queryClient.invalidateQueries({ queryKey: ['orders', 'cooking'] });
      queryClient.invalidateQueries({ queryKey: ['orders', dataSuccess.orderId ?? ''] });
    },
  });
  const { elapsedTime, formatElapsedTime, getColor } = useCronometreOrder(orderDetail, 300);
  const handleChangeStatus = useCallback(
    async (status: OrderDetailStatus) => {
      await mutateAsync({ id: orderDetail?.id, body: { status } });
    },
    [mutateAsync, orderDetail?.id]
  );

  return (
    <Card className="w-[300px] md:w-auto max-w-[340px] overflow-visible lg:overflow-hidden lg:landscape:overflow-hidden landscape:overflow-visible shadow-neutral-100 shadow-md">
      <CardHeader className="justify-between">
        <div className="flex gap-3">
          <Stopwatch color={getColor()} timer={formatElapsedTime(elapsedTime)} />
          <div className="flex flex-col gap-1 items-start justify-center">
            <h4 className="text-small font-semibold leading-none text-default-600">{table}</h4>
            <div className="flex flex-col">
              <h5 className="text-small tracking-tight text-default-400 flex flex-col ">
                {orderId}
              </h5>
              <caption className="text-xs tracking-tight text-default-400 flex flex-row w-[8.5rem] lg:w-auto">
                {dayjs(orderDetail?.createdAt).format('DD/MM/YYYY hh:MM a')}
              </caption>
            </div>
          </div>
        </div>
        <div className=" flex flex-row gap-2">
          <Button
            isIconOnly
            color="default"
            radius="full"
            size="sm"
            onPress={() => {
              onChangeLoading(true);
              onOpen('order', orderDetail?.orderId);
            }}
          >
            <Icon
              color={(dittoTheme?.colors?.primary as any)[100]}
              fontSize={20}
              fontWeight="bold"
              icon="fa-solid:info"
            />
          </Button>
          <Button
            isIconOnly
            color="default"
            radius="full"
            size="sm"
            onPress={() => {
              onChangeLoading(true);
              onOpen('recipe', orderDetail);
            }}
          >
            <Icon
              color={(dittoTheme?.colors?.primary as any)[100]}
              fontSize={25}
              fontWeight="bold"
              icon="mdi:restaurant-menu"
            />
          </Button>
        </div>
      </CardHeader>
      <CardBody className="px-5 py-0 text-small text-default-500">
        <div className="flex flex-row justify-start items-center gap-2 pl-14">
          <strong>{orderDetail?.quantity}x</strong> {orderDetail?.product?.name}
        </div>
      </CardBody>
      <CardFooter className="gap-3 flex flex-row justify-between">
        {orderDetail?.status !== OrderDetailStatus.created ? (
          <Button
            isIconOnly
            color="warning"
            radius="full"
            size="sm"
            onPress={() => handleChangeStatus(prevStatus)}
          >
            <Icon
              color={(dittoTheme?.colors?.grey as any)[0]}
              fontSize={25}
              fontWeight="bold"
              icon="fa-solid:arrow-left"
            />
          </Button>
        ) : (
          <div />
        )}
        {orderDetail?.status !== OrderDetailStatus.done && (
          <Button
            isIconOnly
            color="warning"
            radius="full"
            size="sm"
            onPress={() => handleChangeStatus(nextStatus)}
          >
            <Icon
              color={(dittoTheme?.colors?.grey as any)[0]}
              fontSize={25}
              fontWeight="bold"
              icon="fa-solid:arrow-right"
            />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
